import RelayFeatureFlags from 'relay-runtime/lib/util/RelayFeatureFlags';
import { combineReducers, applyMiddleware, createStore, compose } from 'redux';
import thunk from 'redux-thunk';
import serverVars from 'server-vars';
import { clientIntlPolyfill } from '../../exports/clientIntlPolyfill';
import { VisitorTracking } from 'dibs-visit-tracking/exports/visitorTracking';
import {
    isMasqueradingAsBuyer,
    cookieJarConstants,
    getBuyerToken,
    getBuyerId,
} from 'dibs-cookie-jar';
import Cookies from 'js-cookie';
import { localStorage, sessionStorage } from 'dibs-browser-storage';
import { getBuyerHeaderClient } from './header';
import { getBuyerFooterClient } from './footer';
import { getBuyerCookieNotificationClient } from './cookieNotification';
import { setUserRegionInfo } from './setUserRegionInfo';
import initializeTracking from '../utils/initializeTracking';
import dblReducers from '../reducers';
import { actionCreators } from '../actions/index';
import createRelayEnvironment from '../relayClientEnvironment';
import { triggerAuthModal } from '../utils/AuthModalCheck';
import { loadWidgetButtons } from '../utils/pageWidgetButtons';
import { authorizeToken } from '../helpers/authorizeToken';
import { getUrlWithoutEmailToken } from '../helpers/queryParamsHelpers';
import { ON_SITE_REGISTRATION } from '../authModal/authentication/userStorageConstants';
import { WINDFALL_LOCAL_STORAGE_KEY, isWindfallReferral } from '../helpers/windfallHelper';
import { setTradeCampaign } from '../helpers/isLoggedOutTradeHelper';
import { initCustomPerformanceReporting } from '../helpers/perfTrackingHelpers';
import { setNonTradeUserToSuspectedTradeMutation } from '../mutations/setNonTradeUserToSuspectedTradeMutation';
import { loadSocialNetworks } from '../utils/loadSocialNetworks';
import initAutopilot, { AUTOPILOT_SESSION_STORAGE_KEY } from '../helpers/initAutopilot';

import '../scss/baseBuyerStyles.scss';

// todo remove when https://github.com/facebook/relay/commit/061a6800b81ef49efe7989537e3e769d26397c51 is released.
RelayFeatureFlags.ENABLE_RELAY_CONTAINERS_SUSPENSE = false;

const {
    USER,
    MASQUERADE_BUYER,
    MASQUERADE_BUYER_USER_TOKEN,
    USER_EMAIL,
    USER_TYPE,
    REAL_USER_TYPE,
    USER_EMAIL_TOKEN,
} = cookieJarConstants;

const dblMiddleware = [thunk];

// will be used to store the queue so that functions exported from dbl can interact with it
let queue;

function createGlobalStore(reducers, middlewares, state) {
    const reducer = combineReducers({ ...reducers, ...dblReducers });
    const middleware = middlewares
        .concat(dblMiddleware)
        .filter((value, index, self) => self.indexOf(value) === index);

    let enhancer;
    if (process.env.NODE_ENV !== 'production') {
        const { composeWithDevTools } = require('redux-devtools-extension');
        enhancer = composeWithDevTools;
    } else {
        enhancer = compose;
    }

    return createStore(reducer, state, enhancer(applyMiddleware(...middleware)));
}

Cookies.defaults = {
    domain: serverVars.get('cookieDomain'),
    expires: 182,
    path: '/',
};

function logoutFunc() {
    if (isMasqueradingAsBuyer(document.cookie)) {
        Cookies.remove(MASQUERADE_BUYER);
        Cookies.remove(MASQUERADE_BUYER_USER_TOKEN);
        /*
         * TRADE-3112
         *
         * The USER_TYPE cookie is handled differently than the masquerade
         * cookie -- the real user's type is stored in REAL_USER_TYPE, and
         * the masquerade user's type is used to set USER_TYPE.
         *
         * On ending a masqueradee session, read the REAL_USER_TYPE cookie
         * and, if present, use that to re-populate the USER_TYPE cookie.
         */
        const realUserType = Cookies.get(REAL_USER_TYPE);
        if (realUserType) {
            Cookies.remove(USER_TYPE);
            Cookies.remove(REAL_USER_TYPE);
            Cookies.set(USER_TYPE, realUserType);
        }
    } else {
        Cookies.remove(USER);
        Cookies.remove(USER_EMAIL);
        Cookies.remove(USER_TYPE);
        Cookies.remove(USER_EMAIL_TOKEN);

        const sessionId = window.Autopilot?.sessionId || window.AutopilotAnywhere?.sessionId;

        if (sessionId) {
            // this is used to reset Autopilot session so that when new user logs in new sessionId would be generated
            // eslint-disable-next-line new-cap
            window.Autopilot.GDPRCookieOptOut();
            sessionStorage.deleteItem(AUTOPILOT_SESSION_STORAGE_KEY);
        }
    }
    const queryParams = new URLSearchParams(location.search);
    if (queryParams.get('emailToken')) {
        location.assign(getUrlWithoutEmailToken(queryParams));
    } else {
        location.reload();
    }
}

function initVisitorTracking() {
    new VisitorTracking();
}

/**
 * upon page load, we try to validate the buyer token in your cookies (userToken or emailToken)
 * if you don't have one, continue as normal
 * if you do have one, we try to look up the user associated with the token (see authorizeToken.js)
 * if the user exists, the token is valid and we continue as normal
 * if the user does not exist, the token is invalid. in this case we clear your auth cookies (logging you out), and refresh
 * NOTE: we only execute the DNE case for an explicit 401 on the userID -- see authorizeToken.js and the isUserTokenValidQuery in graphql for more detail
 * this is so that we don't accidentally log people out if our identity service is down, if graphql down, etc.
 * @param {*} relayEnv relay environment under which to make the validation query
 * @param {*} store redux store
 */
function _buyerTokenValidityHandler(relayEnv) {
    const { userToken, emailToken } = getBuyerToken(document.cookie);
    if (emailToken && !Cookies.get(ON_SITE_REGISTRATION)) {
        try {
            window.newrelic.addPageAction('email-only|auto-logout', { emailToken });
        } catch (e) {
            // fail silently
        }
        logoutFunc();
        return;
    }
    const token = userToken || emailToken;
    if (token) {
        authorizeToken(token, relayEnv).then(isValidToken => {
            if (!isValidToken) {
                logoutFunc();
            }
        });
    }
}

function _handleWindfall(relayEnv) {
    if (isWindfallReferral()) {
        const isUserTypeTrade = serverVars.get('settings.isUserTypeTrade') || false;
        const userId = getBuyerId(document.cookie);
        if (!isUserTypeTrade) {
            if (userId) {
                // if the user is logged in, set their trade status to SUSPECTED
                setNonTradeUserToSuspectedTradeMutation(relayEnv, {
                    userId: userId,
                });
            } else {
                // otherwise store the windfall flag in local storage
                localStorage.setItem(WINDFALL_LOCAL_STORAGE_KEY, true);
            }
        }
    }
}

/**
 * @param {object} options
 */
function _initialize(options = {}) {
    const { reducers = {}, middleware = [], logoutUser = logoutFunc, state = {} } = options;

    if (
        state.hasOwnProperty('cart') ||
        state.hasOwnProperty('header') ||
        state.hasOwnProperty('currency')
    ) {
        throw new Error(`state cannot include reserved keys: cart, header, currency`);
    }

    // clone data with stringify/parse to ensure relay does not delete the object references when
    // passed to multiple relay environments
    const data = JSON.parse(
        JSON.stringify(serverVars.get('dbl.relayData')) ||
            serverVars.get('headerRelayPreloadedData')
    );
    const relayEnv = createRelayEnvironment(data);
    _buyerTokenValidityHandler(relayEnv);
    _handleWindfall(relayEnv);

    const { header, cart } = serverVars.get('header') || {};
    const { currency } = serverVars.get('footer') || {};

    if (!header) {
        throw new Error('Header data missing from __SERVER_VARS__.');
    }

    header.logoutUser = logoutUser;

    const store = createGlobalStore(reducers, middleware, { header, currency, cart, ...state });
    const actions = {
        reinitTracking: () => {
            store.dispatch(actionCreators.resetTrackingFired());
        },
    };

    getBuyerHeaderClient({
        node: document.getElementById('js-header'),
        store,
        relayEnv,
    });
    getBuyerFooterClient({
        node: document.getElementById('js-footer'),
        store,
        relayEnv,
    });
    getBuyerCookieNotificationClient({
        node: document.getElementById('js-cookie-notification'),
        relayEnv,
    });

    setUserRegionInfo({
        relayEnv,
        cookieDomain: serverVars.get('cookieDomain'),
        hasRegionsCookieValue: !!Cookies.get('regions'),
    });

    triggerAuthModal(relayEnv);

    initVisitorTracking();
    setTradeCampaign();
    loadSocialNetworks({ relayEnvironment: relayEnv });
    initCustomPerformanceReporting();
    initAutopilot({ environment: relayEnv, userId: getBuyerId(document.cookie) });

    return { store, relayEnv, actions };
}

function _initializeResponsive(options = {}, NotificationQueue, customNotificationActionTypes) {
    const { notifications = [], autoRunNotificationQueue = true, ...otherOptions } = options;
    const { store, actions, relayEnv } = _initialize(otherOptions);
    const isTextToTopEnabled = serverVars.get('enablers.isTextToTopEnabled');
    loadWidgetButtons(isTextToTopEnabled);
    queue = new NotificationQueue(
        notifications,
        store,
        relayEnv,
        autoRunNotificationQueue,
        customNotificationActionTypes
    );
    return { store, actions };
}

function initializeDBL(options = {}) {
    const { initializers = [], customNotificationActionTypes = {}, ...otherOptions } = options;

    const localeDataKey = serverVars.get('localeDataKey') || 'en';
    initializers.push(clientIntlPolyfill(localeDataKey));

    const queuePromise = import(
        /* webpackChunkName: "NotificationQueue" */ '../notificationQueue/Queue'
    );

    return Promise.all([queuePromise, ...initializers]).then(
        ([{ default: NotificationQueue }, ...values]) => {
            const { store, actions } = _initializeResponsive(
                otherOptions,
                NotificationQueue,
                customNotificationActionTypes
            );
            const publicQueue = {
                _getNotificationQueue() {
                    return queue._getNotificationQueue();
                },
                runNotificationQueue() {
                    // Increases visit counter before re-run of queue.
                    initVisitorTracking();
                    queue.runQueue();
                },
            };

            return {
                values,
                store,
                queue: publicQueue,
                actions,
            };
        }
    );
}

function initializeDBLMobile(options = {}) {
    const { initializers = [], ...otherOptions } = options;

    const localeDataKey = serverVars.get('localeDataKey') || 'en';
    initializers.push(clientIntlPolyfill(localeDataKey));

    return Promise.all(initializers).then(values => {
        const { store, actions } = _initialize(otherOptions);
        return { values, store, actions };
    });
}

function runNotificationQueue() {
    queue.runQueue();
}

export {
    initializeTracking, // abc, abt
    initializeDBL, // all consumers
    initializeDBLMobile, // abf
    runNotificationQueue, // abf, abm
    logoutFunc, // exported for tests, should make separate module instead
};
